import { TaxRateSelect } from '@components'

import {
  Attachment,
  AttachmentFile,
  Button,
  ButtonsGroup,
  Flex,
  FormItemsGroup,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalProps,
  notify,
  Space,
  Spacing,
  Text,
  useDropzoneInput,
  useModal,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChooseAttachmentModal } from '@features/attachments/elements/ChooseAttachmentModal'
import { openFileDialog } from '@features/attachments/elements/ChooseFileDropdown/utils/openFileDialog'
import { useUploadFiles } from '@features/attachments/hooks/useUploadFiles'
import { AccountSelectWithCreate } from '@features/chartOfAccounts/elements/AccountSelectWithCreate'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { ModalId } from '../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { useForm } from '../../../../../../hooks'
import { APIError } from '../../../../../../utils'
import { getErrorMessage } from '../../../../../../utils/getErrorMessage'
import { useCreateManualPaymentReconciliation } from './hooks/useCreateManualPaymentReconciliation'
import {
  CreateManualPaymentForm,
  CreateManualPaymentFormSchema,
  getDefaultValues,
  getValidationSchema,
} from './utils/formData'
import { getTransactionPayload } from './utils/getTransactionPayload'

interface CreateManualPaymentModalProps extends ModalProps {
  transaction: BankLine | null
  nextVoucherNumber?: number
}

export const CreateManualPaymentModal = ({
  id: modalId,
  transaction,
  nextVoucherNumber,
}: CreateManualPaymentModalProps): ReactElement | null => {
  const { t } = useTranslation()
  const [attachmentsFiles, setFormAttachmentsFiles] = useState<AttachmentFile[]>([])
  const { organization } = useUserOrganization()
  const { close: closeManualPaymentModal } = useModal(modalId)
  const { open: openChooseFromUploadsModal } = useModal(ModalId.AttachmentsModal)

  const { Form, FormItem, handleSubmit } = useForm<CreateManualPaymentFormSchema>({
    defaultValues: useMemo(() => getDefaultValues(transaction, nextVoucherNumber), [transaction, nextVoucherNumber]),
    validationSchema: useMemo(() => getValidationSchema(t), [t]),
  })

  const { createManualPaymentReconciliation, isProcessing } = useCreateManualPaymentReconciliation({
    contraAccountId: transaction?.accountId || '',
    matchId: transaction?.match?.id || '',
    onSuccess: () => {
      closeManualPaymentModal()
    },
    onError: (error?: APIError) => {
      notify({
        id: NotificationKeys.CreateManualPaymentReconciliation,
        variant: 'error',
        message: getErrorMessage(error) || t('something_went_wrong'),
      })
    },
  })

  const { uploadFiles, isUploading } = useUploadFiles({
    onUpload(file: AttachmentFile) {
      setFormAttachmentsFiles((prev) => [...prev, file])
    },
  })

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      uploadFiles(files)
    },
    [uploadFiles],
  )

  const handleAttachmentSelected = useCallback(
    (attachment: Attachment) => {
      setFormAttachmentsFiles((prev) => [...prev, { attachmentId: attachment.id, ...attachment.file }])
    },
    [setFormAttachmentsFiles],
  )

  const handleRemoveAttachment = useCallback(
    (fileId: string) => {
      setFormAttachmentsFiles((prev) => prev.filter((file) => file.id !== fileId))
    },
    [setFormAttachmentsFiles],
  )

  const { inputRef, inputProps } = useDropzoneInput({ onFilesSelect: handleFilesSelected, multiple: true })

  const handleUploadFromDeviceClick = useCallback(() => {
    openFileDialog(inputRef)
  }, [inputRef])

  const handleUploadFromUploadsClick = useCallback(() => {
    openChooseFromUploadsModal()
  }, [openChooseFromUploadsModal])

  const submitForm = useCallback(() => {
    if (!organization || !transaction) {
      return
    }

    handleSubmit((data: CreateManualPaymentForm) => {
      const payload = getTransactionPayload({
        transaction,
        organization,
        formData: data,
        attachmentsFiles,
      })

      if (payload) {
        createManualPaymentReconciliation(payload)
      }
    })()
  }, [attachmentsFiles, handleSubmit, organization, transaction, createManualPaymentReconciliation])

  if (!transaction) {
    return null
  }

  return (
    <>
      <Modal id={modalId} autofocusOnInput size="s">
        <Modal.Header title={t('create_manual_payment_modal.title')} />
        <Modal.Body>
          <Form>
            <FormItemsGroup rowSpacing={Spacing.S16} itemsInRow={2} itemsTemplate={[6, 2]}>
              <FormItem
                name="description"
                label={t('create_manual_payment_modal.form.name')}
                render={({ field: { value, onChange, ...rest } }) => (
                  <Input {...rest} value={value} onChange={onChange} />
                )}
              />
              <FormItem
                name="receiptNumber"
                label={t('create_manual_payment_modal.form.receipt_number')}
                render={({ field: { value, onChange, ...rest } }) => (
                  <Input {...rest} value={value} onChange={onChange} />
                )}
              />
            </FormItemsGroup>
            <Space size="s16" />
            <FormItemsGroup rowSpacing={Spacing.S16}>
              <FormItem
                name="accountId"
                label={t('create_manual_payment_modal.form.account')}
                render={({ field: { value, onChange, ...rest } }) => (
                  <AccountSelectWithCreate
                    {...rest}
                    dropdownSize="fitTrigger"
                    selectedId={value}
                    onSelect={(id, value) => {
                      onChange(value?.id)
                    }}
                  />
                )}
              />
              <FormItem
                name="vatRateId"
                label={t('create_manual_payment_modal.form.vat_rate')}
                render={({ field: { value, onChange, ...rest } }) => (
                  <TaxRateSelect {...rest} dropdownSize="fitTrigger" selectedId={value} onSelect={onChange} />
                )}
              />
            </FormItemsGroup>
          </Form>
          <Space size="s8" />
          <ButtonsGroup direction="column">
            <Button variant="text" onClick={handleUploadFromDeviceClick} icon="paperclip" loading={isUploading}>
              {t('create_manual_payment_modal.attachments_device_label')}
            </Button>
            <Button variant="outlined" onClick={handleUploadFromUploadsClick}>
              {t('create_manual_payment_modal.attachments.uploads_label')}
            </Button>
          </ButtonsGroup>

          <input ref={inputRef} {...inputProps} />
          <Space size="s16" />
          {attachmentsFiles.map((file) => (
            <Flex key={file.id} gap={Spacing.S8} alignItems="center">
              <Icon icon="paperclip" />
              <Text>{file.fileName}</Text>
              <IconButton size="sm" icon="trash" onClick={() => handleRemoveAttachment(file.id)} />
            </Flex>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={submitForm} loading={isProcessing}>
            {t('create_manual_payment_modal.submit')}
          </Button>
        </Modal.Footer>
      </Modal>

      <ChooseAttachmentModal id={ModalId.AttachmentsModal} onSelect={handleAttachmentSelected} size="xl" />
    </>
  )
}
